import { importDraftKey } from './crypto.ts';
import { drafts, keys } from './store.ts';

export type DraftKey = { keyId: string; key: CryptoKey };

/**
 * The draft key of the signed-in person on this browser (roadmap 3.3).
 *
 * - Standard mode: the server's per-(person, device) key, imported non-extractable and kept in IndexedDB,
 *   so drafts open after a reload even offline. It survives idle expiry and dies with explicit sign-out.
 * - Shared mode: the server's per-session key, held in memory only and fetched again after a reload.
 *
 * Crypto-shredding: when the server key row is destroyed (sign-out, shared-mode expiry), what stays on
 * disk is ciphertext nobody can open. `prepare()` then removes it: every draft whose key id is not the
 * current one, and everything belonging to another person. The second rule is a privacy choice: a
 * browser that a second person signs in to in standard mode is being shared, and the first person's
 * drafts and stored key must not stay usable there (spec §1; shared computers should use shared mode).
 */
export class DraftKeyring {
  readonly personId: string;
  readonly #mode: 'standard' | 'shared';
  readonly #fetchKey: () => Promise<{ keyId: string; keyB64: string }>;
  #current: Promise<{ key: DraftKey; fromServer: boolean } | null> | null = null;

  constructor(personId: string, mode: 'standard' | 'shared', fetchKey: () => Promise<{ keyId: string; keyB64: string }>) {
    this.personId = personId;
    this.#mode = mode;
    this.#fetchKey = fetchKey;
  }

  #load(): Promise<{ key: DraftKey; fromServer: boolean } | null> {
    this.#current ??= (async () => {
      try {
        const k = await this.#fetchKey();
        const key = { keyId: k.keyId, key: await importDraftKey(k.keyB64) };
        if (this.#mode === 'standard') await keys.put({ personId: this.personId, ...key }).catch(() => {});
        return { key, fromServer: true };
      } catch {
        this.#current = null;   // try the server again next time
        if (this.#mode !== 'standard') return null;
        const stored = await keys.get(this.personId).catch(() => undefined);
        return stored ? { key: { keyId: stored.keyId, key: stored.key }, fromServer: false } : null;
      }
    })();
    return this.#current;
  }

  /** The key to encrypt and decrypt with, or null when there is none (offline shared session). */
  async current(): Promise<DraftKey | null> {
    return (await this.#load())?.key ?? null;
  }

  /** Purges what can no longer be opened. Only a key the server just confirmed can judge staleness. */
  async prepare(): Promise<{ purged: number }> {
    const loaded = await this.#load();
    if (!loaded?.fromServer) return { purged: 0 };
    let purged = 0;
    for (const d of await drafts.all()) {
      if (d.personId !== this.personId || d.keyId !== loaded.key.keyId) {
        await drafts.delete(d.id);
        purged++;
      }
    }
    for (const k of await keys.all()) if (k.personId !== this.personId) await keys.delete(k.personId);
    return { purged };
  }

  forget(): void {
    this.#current = null;
  }
}
